import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { SelectOption } from '@bcl/design-system';
import { MonitorWithOptions } from 'src/app/pages/area/area.model';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { BaseAreaMonitorAccordionComponent } from '../base-accordion.component';
import { AccordionService } from 'src/app/services/accordion.service';

@Component({
  selector: 'app-area-monitor-status',
  templateUrl: './area-monitor-status.component.html',
  styleUrls: ['./area-monitor-status.component.scss'],
})
export class AreaMonitorStatusComponent
  extends BaseAreaMonitorAccordionComponent
  implements OnInit, OnDestroy
{
  @Input() monitor: MonitorWithOptions;
  @Input() visit?: any; // Add visit input
  @Input() isEditingDisabled = false;
  @Input() isProcessing = false;
  @Output() observationChange = new EventEmitter<ObservationDetails>();

  public selectedAnswers: { [key: number]: string } = {};
  public questionsOptions: { [key: number]: SelectOption[] } = {};
  public form: FormGroup;
  public filteredObservationStatuses: SelectOption[] = [];

  public lastObservationId: string;
  private subscription: any;

  constructor(private fb: FormBuilder, accordionService: AccordionService) {
    super(accordionService);
  }

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled || this.lastObservationId !== '1';
  }
  get isButtonsDisabled(): boolean {
    return this.isEditingDisabled || this.isProcessing;
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.monitor?.observation?.id) {
      this.lastObservationId = this.monitor.observation.id.toString();
    }
    const tempObservationDetails = JSON.parse(
      this.monitor?.observation?.details || '{}'
    );

    this.filterObservationStatuses();

    // Create form group with dynamic controls
    const formGroup: { [key: string]: any } = {};
    for (const question of this.monitor?.observationQuestions || []) {
      this.questionsOptions[question.id] = this.mapOptionsToSelect(
        question.options
      );
      this.selectedAnswers[question.id] =
        tempObservationDetails[question.id] || null;
      formGroup[question.id] = [tempObservationDetails[question.id] || null];
    }

    this.form = this.fb.group(formGroup);

    this.subscription = this.form.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((values) => {
        this.selectedAnswers = values;
        this.changeObservationAnswers();
      });

    // Disable form if visit is completed and not reopened
    if (this.isFormDisabled) {
      this.form.disable();
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  changeObservation(observationId: string) {
    this.lastObservationId = observationId;
    if (this.isFormDisabled) {
      this.form.disable({ emitEvent: false });
      // Set all answers to null when form is disabled
      for (const questionId in this.selectedAnswers) {
        if (this.selectedAnswers.hasOwnProperty(questionId)) {
          this.selectedAnswers[questionId] = null;
        }
      }
      // Set all observation questions to null when form is disabled
      for (let i = 0; i < this.monitor.observationQuestions.length; i++) {
        this.monitor.observationQuestions[i] = null;
      }
      // Set all form values to null when form is disabled
      for (const questionId in this.selectedAnswers) {
        if (this.selectedAnswers.hasOwnProperty(questionId)) {
          this.form.get(questionId)?.setValue(null, { emitEvent: false });
        }
      }
    } else {
      this.form.enable({ emitEvent: false });
    }
    this.emitObservationChange();
  }

  changeObservationAnswers() {
    if (this.lastObservationId && !this.isFormDisabled) {
      const observationDetails = {
        id: this.lastObservationId,
        details: JSON.stringify(this.selectedAnswers),
      };
      this.observationChange.emit(observationDetails);
    }
  }

  mapOptionsToSelect(options: any[]) {
    return options.map((option) => ({
      value: option.id.toString(),
      label: option.name,
    }));
  }

  handleCounterInput(value: number, questionId: number) {
    if (value < 0) {
      this.form.get(questionId.toString())?.setValue(0, { emitEvent: false });
    }
  }

  private emitObservationChange() {
    if (this.lastObservationId) {
      const observationDetails = {
        id: this.lastObservationId,
        details: JSON.stringify(this.selectedAnswers),
      };
      this.observationChange.emit(observationDetails);
    }
  }

  private filterObservationStatuses() {
    if (this.monitor?.statuses && this.monitor.statuses.length > 0) {
      this.filteredObservationStatuses = (
        this.monitor.observationStatuses || []
      ).filter((status) => this.monitor.statuses!.includes(status.value));
    } else {
      this.filteredObservationStatuses =
        this.monitor?.observationStatuses || [];
    }
  }
}

export type ObservationDetails = {
  id: string;
  details: string;
};
